import { useRef } from "react";
import { saveAs } from "file-saver";
import {
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  EllipsisIcon,
  FileTextIcon,
} from "lucide-react";

import { AudioPlayer, Column, Row, Text } from "@/components";
import { CallIcon } from "@/components/CallIcon";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { dates } from "@/lib/dates";
import { misc } from "@/lib/misc";
import { transcript } from "@/lib/transcript";
import { CallHistoryStafferCall } from "@/types";

// Compact staffer-call card interweaved into the messages timeline. The top
// line stays staffer-name-only; the timestamp lives on the bottom strip.
export const MessageCallCard = ({
  call,
  candidateFirstName,
}: {
  call: CallHistoryStafferCall;
  candidateFirstName: string;
}) => {
  /* React Hooks */
  const audioRef = useRef<HTMLAudioElement>(null);
  const successToast = useSuccessToast();

  /* Helpers */
  const stafferName =
    `${call.user.firstName} ${call.user.lastName}`.trim() || "Staffer";
  const transcriptLines = transcript.parseLines(call.transcript);
  const durationLabel =
    call.duration !== null
      ? `${Math.floor(call.duration / 60).toString()}:${String(call.duration % 60).padStart(2, "0")}`
      : null;

  /* Render */
  const renderActionsMenu = () => {
    if (!transcriptLines?.length && !call.audioUrl) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-4 w-4 shrink-0 p-0"
            size="icon"
            type="button"
            variant="ghost"
          >
            <EllipsisIcon className="h-4 w-4 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {transcriptLines?.length ? (
            <DropdownMenuItem
              onClick={() => {
                const text = misc.buildClipboardTextForTranscript({
                  getRoleLabel: (role) =>
                    role === "staffer" ? stafferName : candidateFirstName,
                  transcriptLines,
                });
                void navigator.clipboard.writeText(text);
                successToast({
                  description: "Copied transcript to clipboard!",
                });
              }}
            >
              <CopyIcon className="h-4 w-4" />
              Copy full transcript
            </DropdownMenuItem>
          ) : null}
          {call.audioUrl ? (
            <DropdownMenuItem
              onClick={() => {
                if (!call.audioUrl) return;
                saveAs(
                  call.audioUrl,
                  `staffer-call-${candidateFirstName}-${call.startedAt}.mp3`
                    .toLowerCase()
                    .replace(/\s+/g, "-"),
                );
              }}
            >
              <DownloadIcon className="h-4 w-4" />
              Download audio
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderTranscript = () => {
    if (!transcriptLines?.length) return null;

    return (
      <>
        <CollapsibleTrigger asChild>
          <Button
            className="h-auto w-fit gap-1 p-0 text-xs font-normal text-slate-400 hover:text-slate-700"
            type="button"
            variant="ghost"
          >
            <FileTextIcon className="h-3 w-3 shrink-0" />
            <Text className="text-xs font-normal text-current group-data-[state=closed]/message-call:inline group-data-[state=open]/message-call:hidden">
              Show transcript
            </Text>
            <Text className="text-xs font-normal text-current group-data-[state=open]/message-call:inline group-data-[state=closed]/message-call:hidden">
              Hide transcript
            </Text>
            <ChevronRightIcon className="h-3 w-3 shrink-0 transition-transform duration-200 group-data-[state=open]/message-call:rotate-90" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-2">
            {transcriptLines.map((line, index) => (
              <Text
                className="text-sm font-normal leading-relaxed text-slate-600"
                key={`${line.time}-${String(index)}`}
              >
                {`${line.role === "staffer" ? stafferName : candidateFirstName}: ${line.content}`}
              </Text>
            ))}
          </Column>
        </CollapsibleContent>
      </>
    );
  };

  const render = () => {
    return (
      <Collapsible className="group/message-call overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <Row className="min-w-0 items-center justify-between gap-2 px-4 py-3">
          <Row className="min-w-0 items-center gap-2">
            <CallIcon
              circleClassName="h-6 w-6"
              className="h-3 w-3 text-slate-400"
              direction={call.direction}
              status={call.status}
            />
            <Text className="truncate text-sm font-medium text-slate-700">
              {stafferName}
            </Text>
          </Row>
          {renderActionsMenu()}
        </Row>
        <Column className="gap-2 bg-slate-50 px-4 py-3">
          {call.audioUrl ? (
            <AudioPlayer audioRef={audioRef} audioSrc={call.audioUrl} />
          ) : null}
          <Text className="text-xs font-normal text-slate-400">
            {dates.formatShortDateTime(call.startedAt)}
            {durationLabel ? ` · ${durationLabel}` : ""}
          </Text>
          {renderTranscript()}
        </Column>
      </Collapsible>
    );
  };

  return render();
};
